/*
 * ScaniaConnectionsView — add and remove a fleet's Scania credentials from inside the app.
 *
 * NAMED FOR THE MODEL, not for what the screen is called. The record is a `ScaniaConnection`
 * and the endpoint is `/connections/`, so this file was the only place using a third word for
 * the same thing.
 *
 * WHAT THE SCREEN IS NOT. It is not the access control. The API refuses a non-administrator
 * with a 404 whatever this component renders, and the secret is write-only server-side. A
 * screen that hides a button is a screen, not a permission.
 *
 * The demo this exists for: remove a credential and watch the fleet's locations stop, add it
 * back and watch them return. Deleting evicts the cached bearer token, so revocation bites
 * immediately rather than whenever the token would have expired.
 */

import React from 'react';
import { translate } from 'react-i18next';

import CBMediaQuery from '../shared/CBMediaQuery';
import CBMobileCard from '../shared/CBMobileCard';
import CBPromptModal from '../shared/CBPromptModal';
import { getCsrfToken, getStyle } from '../../utils/utils';
import { fleetLocationStyles as styles } from './fleetLocationStyles';
import { scaniaConnectionStyles as onboardingStyles } from './scaniaConnectionStyles';
import InlineNameEditor from './InlineNameEditor';
import ScaniaConnectionDialog from './ScaniaConnectionDialog';
import { connectionLabel } from './fleetMapCore';

const CONNECTIONS_URL = '/uiapi/1/fleetlocation/connections/';

function request(url, options) {
  const config = Object.assign(
    {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRFToken': getCsrfToken(),
      },
    },
    options || {}
  );
  return fetch(url, config).then((resp) => {
    if (resp.status === 204) {
      return null;
    }
    return resp.json().then((payload) => {
      if (!resp.ok) {
        const error = new Error('http ' + resp.status);
        error.status = resp.status;
        error.payload = payload;
        throw error;
      }
      return payload;
    });
  });
}

/** The pinned error vocabulary, rendered in words a fleet administrator can act on.
 *
 * Takes the translator rather than reaching for one: the WIRE vocabulary is fixed and
 * English ('invalid_request'), the words shown to a person are not.
 */
function messageFor(error, t) {
  const detail = error.payload && error.payload.detail ? error.payload.detail : '';
  if (error.status === 404) {
    return t('errorNotAdmin');
  }
  if (detail === 'invalid_request') {
    return t('errorMissingFields');
  }
  if (error.status === 429) {
    return t('errorTooManyAttempts');
  }
  return t('errorSaveFailed');
}

class ScaniaConnectionsView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      status: 'loading',
      connections: [],
      busy: false,
      error: '',
      dialogOpen: false,
      detailsOpen: false,
      // The credential awaiting confirmation, or null. Removing one is IRREVERSIBLE: the
      // secret is stored encrypted and never returned, so a mis-click costs a trip to
      // Scania's portal. The rest of the product asks first; this screen did not.
      pendingRemoval: null,
    };
    this.mounted = false;
    this.scopeGeneration = 0;
    this.adding = false;
  }

  componentDidMount() {
    this.mounted = true;
    this.reload();
  }

  componentDidUpdate(prevProps) {
    if (this.organizationId(prevProps) !== this.organizationId()) {
      this.scopeGeneration += 1;
      this.adding = false;
      this.setState({
        status: 'loading',
        connections: [],
        pendingRemoval: null,
        dialogOpen: false,
        detailsOpen: false,
        busy: false,
        error: '',
      });
      this.reload();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    this.scopeGeneration += 1;
  }

  organizationId(props = this.props) {
    if (props.organizationId) {
      return props.organizationId;
    }
    if (props.match && props.match.params) {
      return props.match.params.organizationId;
    }
    return null;
  }

  async reload() {
    const org = this.organizationId();
    const generation = this.scopeGeneration;
    if (!org) {
      this.setState({ status: 'error', error: this.props.t('errorNoOrganisation') });
      return;
    }
    try {
      const connections = await request(CONNECTIONS_URL + '?org=' + org, { method: 'GET' });
      if (!this.mounted || generation !== this.scopeGeneration) { return; }
      this.setState({ status: 'ready', connections, error: '' });
    } catch (err) {
      if (!this.mounted || generation !== this.scopeGeneration) { return; }
      this.setState({ status: 'error', error: messageFor(err, this.props.t) });
    }
  }

  async saveName(id, label, signal) {
    const generation = this.scopeGeneration;
    const data = await request(CONNECTIONS_URL + id + '/', {
      method: 'PATCH', body: JSON.stringify({ label }),
      ...(signal ? { signal } : {}),
    });
    if (!data || data.id !== id || typeof data.label !== 'string') { throw new Error('nameSaveFailed'); }
    if (!this.mounted || generation !== this.scopeGeneration || (signal && signal.aborted)) { return; }
    this.setState((prev) => ({
      connections: prev.connections.map((connection) => connection.id === id
        ? { ...connection, label: data.label } : connection),
    }));
  }

  openDialog = () => {
    if (this.state.busy) {
      return;
    }
    this.setState({ dialogOpen: true, error: '' });
  };

  closeDialog = () => {
    this.setState({ dialogOpen: false });
  };

  async add({ clientId, secret, label }) {
    if (this.state.busy || this.adding) {
      return;
    }
    this.adding = true;
    const organization = this.organizationId();
    const generation = this.scopeGeneration;
    this.setState({ busy: true, error: '' });
    try {
      await request(CONNECTIONS_URL, {
        method: 'POST',
        body: JSON.stringify({
          organization,
          client_id: clientId,
          client_secret: secret,
          label,
          enabled: true,
        }),
      });
      if (!this.mounted || generation !== this.scopeGeneration) { return; }
      // THE SECRET LEAVES THE BROWSER THE MOMENT IT IS STORED. Closing the dialog drops
      // the form state; the server never returns the secret.
      this.setState({ busy: false, dialogOpen: false, error: '' });
      await this.reload();
      if (!this.mounted || generation !== this.scopeGeneration) { return; }
      this.notifyChanged();
    } catch (err) {
      if (!this.mounted || generation !== this.scopeGeneration) { return; }
      this.setState({ busy: false, error: messageFor(err, this.props.t) });
    } finally {
      this.adding = false;
    }
  }

  async remove(connection) {
    const generation = this.scopeGeneration;
    this.setState({ busy: true, error: '', pendingRemoval: null });
    try {
      await request(CONNECTIONS_URL + connection.id + '/', { method: 'DELETE' });
      if (!this.mounted || generation !== this.scopeGeneration) { return; }
      this.setState({ busy: false });
      await this.reload();
      if (!this.mounted || generation !== this.scopeGeneration) { return; }
      this.notifyChanged();
    } catch (err) {
      if (!this.mounted || generation !== this.scopeGeneration) { return; }
      this.setState({ busy: false, error: messageFor(err, this.props.t) });
    }
  }

  notifyChanged() {
    // THE MAP MUST REFETCH ITS CONFIGURATION, not merely its vehicles. The map key is
    // withheld while no enabled connection exists, so a customer adding their first
    // credential would otherwise sit looking at a list where a map should have appeared.
    if (this.props.onChanged) {
      this.props.onChanged();
    }
  }

  render() {
    const {
      status, connections, busy, error, pendingRemoval, dialogOpen, detailsOpen,
    } = this.state;
    const { t, organizationName } = this.props;
    return (
      <CBMediaQuery>
        {(screenSize) => (
          <div style={getStyle(screenSize, styles, 'container')}>
            <h2 style={getStyle(screenSize, styles, 'heading')}>{t('credentialsTitle')}</h2>
            <p style={getStyle(screenSize, onboardingStyles, 'intro')}>{t('credentialsIntro')}</p>
            <button
              type="button"
              onClick={this.openDialog}
              disabled={busy}
              style={getStyle(screenSize, onboardingStyles, 'addCta')}
            >
              {t('addCredentialTitle')}
            </button>
            {status === 'loading' && (
              <p style={getStyle(screenSize, styles, 'summary')}>{t('loading')}</p>
            )}
            {error && !dialogOpen && <p style={getStyle(screenSize, styles, 'error')}>{error}</p>}
            {status === 'ready' && (
              <ConnectionTable
                connections={connections}
                busy={busy}
                t={t}
                screenSize={screenSize}
                detailsOpen={detailsOpen}
                onToggleDetails={() => this.setState((prev) => ({ detailsOpen: !prev.detailsOpen }))}
                onRemove={(connection) => this.setState({ pendingRemoval: connection })}
                nameGeneration={this.scopeGeneration}
                onRename={(id, value, signal) => this.saveName(id, value, signal)}
              />
            )}
            <ScaniaConnectionDialog
              isOpen={dialogOpen}
              t={t}
              busy={busy}
              error={dialogOpen ? error : ''}
              organizationName={organizationName}
              onClose={this.closeDialog}
              onSubmit={(fields) => this.add(fields)}
            />
            {/*
              ASK BEFORE AN IRREVERSIBLE DELETE. The secret is stored encrypted and never
              returned, so a mis-click means fetching it again from Scania's portal. The rest
              of the product asks first -- CBPromptModal is imported by 36 files -- and this
              screen was the exception.
            */}
            <CBPromptModal
              modalIsOpen={!!pendingRemoval}
              closeModal={() => this.setState({ pendingRemoval: null })}
              performAction={() => this.remove(pendingRemoval)}
              // NAMED, because an organisation may hold several and "this credential"
              // does not say which one is about to be destroyed. Matches the row's name.
              title={t('removeCredentialTitle', {
                name: pendingRemoval
                  ? connectionLabel(pendingRemoval, t)
                  : '',
              })}
              text={t('removeCredentialText')}
              buttonText={t('removeCredentialButton')}
              cancelText={t('cancel')}
            />
          </div>
        )}
      </CBMediaQuery>
    );
  }
}

/*
 * THE CREDENTIALS, IN WHICHEVER SHAPE THE SCREEN CAN HOLD.
 *
 * The widest column here is an 86-character client id, so this table overflows a phone worse
 * than the fleet one does. On a small screen each credential becomes a card, with Remove in
 * the three-dot menu the rest of the product uses for per-row actions -- and it still routes
 * through the confirmation, because a menu item is even easier to hit by accident.
 */
/*
 * WHAT A CREDENTIAL'S COVERAGE SAYS, in one place because it is read in three. `sync_status`
 * has three legal values and only one of them is an error: "dataact" is the free tier, whose
 * coverage is discovered by probing /fms/latest because that tier publishes no vehicle list
 * (fleetlocation/models.py:45). Testing for "ok" alone called every free-tier row a failure.
 */
function coverageLabel(connection, t) {
  if (connection.sync_status === 'ok') {
    return t('coverageVehicles', { count: connection.vin_count });
  }
  if (connection.sync_status === 'dataact') {
    return t('coverageDataactVehicles', { count: connection.vin_count });
  }
  return t('coverageSyncFailed');
}

function secretHint(connection, t) {
  return connection.secret_hint ? '…' + connection.secret_hint : t('secretHidden');
}

function ConnectionTable({
  connections, busy, onRemove, t, screenSize, nameGeneration, onRename, detailsOpen, onToggleDetails,
}) {
  if (connections.length === 0) {
    return <p style={getStyle(screenSize, styles, 'summary')}>{t('noCredentials')}</p>;
  }
  const detailsToggle = (
    <button
      type="button"
      aria-expanded={detailsOpen ? 'true' : 'false'}
      onClick={onToggleDetails}
      style={getStyle(screenSize, onboardingStyles, 'detailsToggle')}
    >
      {detailsOpen ? t('hideTechnicalDetails') : t('showTechnicalDetails')}
    </button>
  );
  if (screenSize === 'small') {
    return (
      <div>
        {detailsToggle}
        {connections.map((connection) => {
          const label = connectionLabel(connection, t);
          const technical = [
            { label: t('columnClientId'), value: connection.client_id },
            { label: t('columnSecret'), value: secretHint(connection, t) },
          ];
          const coverageField = {
            label: t('columnCoverage'),
            value: coverageLabel(connection, t),
          };
          return (
          <div key={connection.id}>
          <CBMobileCard
            title={label}
            titleControl={<InlineNameEditor key={nameGeneration + ':' + connection.id} value={connection.label}
              display={<span>{label}</span>} contextLabel={label} t={t} onSave={(value, signal) => onRename(connection.id, value, signal)} />}
            dialogTitle={label}
            subtitle={coverageLabel(connection, t)}
            fields={detailsOpen ? technical : []}
            allFields={detailsOpen ? technical.concat([coverageField]) : [coverageField]}
            options={[
              {
                label: t('remove'),
                icon: 'pi pi-trash',
                command: () => {
                  if (!busy) {
                    onRemove(connection);
                  }
                },
              },
            ]}
          />
          </div>
          );
        })}
      </div>
    );
  }
  const header = getStyle(screenSize, styles, 'tableHeader');
  const cell = getStyle(screenSize, styles, 'tableCell');
  const muted = getStyle(screenSize, styles, 'tableCellMuted');
  return (
    <div>
      {detailsToggle}
      <table style={getStyle(screenSize, styles, 'table')}>
        <thead>
          <tr>
            <th style={header}>{t('columnName')}</th>
            {detailsOpen && <th style={header}>{t('columnClientId')}</th>}
            {detailsOpen && <th style={header}>{t('columnSecret')}</th>}
            <th style={header}>{t('columnCoverage')}</th>
            <th style={header} />
          </tr>
        </thead>
        <tbody>
          {connections.map((connection) => {
            const label = connectionLabel(connection, t);
            return (
            <tr key={connection.id}>
              <td style={cell}>
                <InlineNameEditor key={nameGeneration + ':' + connection.id} value={connection.label}
                  display={label} contextLabel={label} t={t} onSave={(value, signal) => onRename(connection.id, value, signal)} />
              </td>
              {detailsOpen && (
                <td style={getStyle(screenSize, styles, 'tableCellToken')}>
                  {connection.client_id}
                </td>
              )}
              {detailsOpen && (
                <td style={muted}>
                  {secretHint(connection, t)}
                </td>
              )}
              <td style={cell}>
                {coverageLabel(connection, t)}
              </td>
              <td style={{ ...cell, textAlign: 'right' }}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onRemove(connection)}
                  style={getStyle(screenSize, styles, 'buttonSmall')}
                >
                  {t('remove')}
                </button>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default translate('FleetLocation')(ScaniaConnectionsView);
